import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import TabbedView from '../../components/shared/tabs/TabbedView';
import { Feature } from 'src/replicant/analytics';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import FriendshipRequestsBadge from '../FriendshipRequestsBadge';
import StateObserver, { user } from 'src/StateObserver';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import {
  getBuruFriends,
  getFriendshipPendingRequests,
} from 'src/replicant/friends/friends.getters';
import type { DialogOrigin } from 'src/lib/analytics/properties';
import { track } from 'src/lib/analytics/utils';
import { TabMyFriends } from '../components/TabMyFriends';
import { TabRequests } from '../components/TabRequests';
import { AssetGroup } from '@play-co/timestep-core';
import { downscale } from 'src/conf/scale';
import { isPlatformNative } from 'src/lib/native/native.util';
import { isShareTargetPickerAvailable } from 'src/lib/sharing';
import {
  shareAddFriendFromFriendListViaLine,
  shareAddFriendFromFriendListViaNativeShare,
} from '../friendsList.share';
import { clearClusterSuggestionsCache } from '../friendSuggestions/friendSuggestions.utils';

type CloseHandler = () => void;

export default class PopupFriendsList extends PopupBasicLazy {
  private tabs: TabbedView;
  private tabMyFriends: TabMyFriends;
  private tabRequests: TabRequests;
  private lineButton?: ButtonScaleView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 940,
      darkerBg: false,
      superview: opts.superview,
      close: opts.close,
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP,
      },
    });

    this.tabs = new TabbedView({
      tabsFillWidth: true,
      tabs: [
        {
          label: () => i18n('friendsList.myFriends'),
          tracking: {
            feature: Feature.FRIENDS._,
            $subFeature: Feature.FRIENDS.TAB_MY_FRIENDS,
          },
          onSelect: () => this.selectTab('friends'),
        },
        {
          label: () => i18n('friendsList.requests'),
          tracking: {
            feature: Feature.FRIENDS._,
            $subFeature: Feature.FRIENDS.TAB_REQUESTS,
          },
          onSelect: () => this.selectTab('requests'),
        },
      ],
      alignment: 'left',
      superview: this.box,
      rect: {
        x: 30,
        y: 140,
        width: this.box.style.width - 60,
        height: 640,
      },
    });

    const [friendsTabView, requestsTabView] = this.tabs.getViews();
    const [, requestsTabButton] = this.tabs.getTabs();
    this.createRequestTabBadge(requestsTabButton.getView());

    // Tab contents
    this.tabMyFriends = new TabMyFriends({
      tab: friendsTabView,
      closePopup: opts.close,
    });

    this.tabRequests = new TabRequests({
      tab: requestsTabView,
      closePopup: opts.close,
    });

    this.createShareCtas();
  }

  public getAssetsGroup() {
    return new AssetGroup(
      AssetGroup.constructURLs(['assets/ui/friends', 'assets/ui/shared']),
    );
  }

  public init(opts: { tab?: 'friends' | 'requests'; origin?: DialogOrigin }) {
    // Refresh "You may know" suggestions on every open: synchronously drop the
    // cached cluster result so the tab refresh below refetches fresh data, and
    // reset each tab's per-open suggestion state (the per-open sent-tile
    // debounce).
    clearClusterSuggestionsCache();
    this.tabMyFriends.resetSuggestions();
    this.tabRequests.resetSuggestions();

    // Native keeps the original "Friends" title; MiniApp uses "Add Friends"
    // per Figma 29746-86182 / 29769-86709 vs 29744-84217.
    const showAddFriendsTitle = !isPlatformNative();
    super.init({
      title: showAddFriendsTitle
        ? i18n('friendsList.addFriendsTitle')
        : i18n('friendsList.title'),
    });

    // Hide the LINE CTA when the LINE Share Target Picker isn't available
    // (e.g. open web).
    if (this.lineButton) {
      this.lineButton.updateOpts({ visible: isShareTargetPickerAvailable() });
    }

    // Auto-select requests tab if there are pending requests to handle
    const hasPendingRequests = getFriendshipPendingRequests(user()).length > 0;
    const defaultTab = hasPendingRequests ? 'requests' : 'friends';
    const selectedTab = opts.tab || defaultTab;
    this.selectTab(selectedTab);

    track('FriendListOpen', {
      popupId: 'popupFriendsList',
      popupOrigin: opts.origin,
      defaultTab: selectedTab === 'friends' ? 'friend_list' : 'friend_request',
      friendCount: getBuruFriends(user())?.length ?? 0,
    });
  }

  private selectTab(tab: 'friends' | 'requests') {
    if (tab === 'friends') {
      this.tabs.setProps({ selectedTabIndex: 0 });
      this.tabMyFriends.refresh();
    }

    if (tab === 'requests') {
      this.tabs.setProps({ selectedTabIndex: 1 });
      this.tabRequests.refresh();
    }
  }

  private createRequestTabBadge(superview: View) {
    new FriendshipRequestsBadge({
      superview,
      size: 40,
      x: superview.style.width - 50,
      y: -12,
      font: 'Body',
      fontSize: 18,
      textColor: 'white',
      style: 'red',
    });
  }

  /**
   * Share CTAs in place of the legacy bottom Close button.
   *
   * Each share path is owned by `friendsList.share.ts`, which stamps
   * `feature: 'friend_list'` and the matching subFeature
   * (`friend_list_line_share` / `friend_list_native_share`) onto the
   * entry payload + sender-side `FriendRequestSend` so receiver events
   * inherit Friend List attribution via `SUB_FEATURE_TO_METHOD`.
   *
   * - **MiniApp (LINE):** two CTAs side-by-side — `ButtonScaleView` with
   *   the locale-specific shared icon assets (`btn_line_${locale}.png` /
   *   `btn_others_${locale}.png`) that bake in both the glyph AND the
   *   text, giving both buttons a matching vertical-divider treatment.
   * - **Native apps:** single centered green "Add Friends" CTA, text
   *   only, no icon (per Figma 29746-86182 / 29769-86709). Title stays
   *   as "Friends" — see `init()`.
   */
  private createShareCtas() {
    const buttonWidth = downscale(389);
    const buttonHeight = downscale(133);
    const buttonY = this.box.style.height - 85;

    if (isPlatformNative()) {
      // Native variant: green text-only "Add Friends" CTA, no icon. The
      // title stays as "Friends" (see init()).
      new ButtonScaleViewWithText({
        superview: this.box,
        ...uiConfig.buttons.secondary,
        labelOffsetY: -1,
        fontSize: 42,
        font: 'Body',
        x: this.box.style.width / 2,
        y: buttonY,
        width: 334,
        height: 100,
        centerOnOrigin: true,
        localeText: () => i18n('friendsList.addFriendsTitle'),
        onClick: async () => {
          sounds.sfx.play('ok');
          await shareAddFriendFromFriendListViaNativeShare();
        },
        tracking: {
          feature: Feature.FRIEND_LIST._,
          $subFeature: Feature.FRIEND_LIST.NATIVE_SHARE,
        },
      });
      return;
    }

    const locale = StateObserver.getLocale();
    const othersIcon = {
      image: `assets/ui/shared/buttons/btn_others_${locale}.png`,
      width: downscale(319),
      height: downscale(90),
      offset: { x: 0, y: -5 },
    };

    const buttonRow = new FlexBoxView({
      superview: this.box,
      x: 0,
      y: buttonY - buttonHeight / 2,
      width: this.box.style.width,
      height: buttonHeight,
      flex: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    const flexMargin = { marginLeft: 5, marginRight: 5 };

    this.lineButton = new ButtonScaleView({
      superview: buttonRow,
      ...uiConfig.buttons.secondary,
      width: buttonWidth,
      height: buttonHeight,
      icon: {
        image: `assets/ui/shared/buttons/btn_line_${locale}.png`,
        width: downscale(321),
        height: downscale(90),
        offset: { x: 0, y: -5 },
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await shareAddFriendFromFriendListViaLine();
      },
      tracking: {
        feature: Feature.FRIEND_LIST._,
        $subFeature: Feature.FRIEND_LIST.LINE_SHARE,
      },
    });
    getFlexNode(this.lineButton).updateStyle(flexMargin);

    const nativeButton = new ButtonScaleView({
      superview: buttonRow,
      ...uiConfig.buttons.blue,
      width: buttonWidth,
      height: buttonHeight,
      icon: othersIcon,
      onClick: async () => {
        sounds.sfx.play('ok');
        await shareAddFriendFromFriendListViaNativeShare();
      },
      tracking: {
        feature: Feature.FRIEND_LIST._,
        $subFeature: Feature.FRIEND_LIST.NATIVE_SHARE,
      },
    });
    getFlexNode(nativeButton).updateStyle(flexMargin);
  }
}
